import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Settings, ShieldCheck, MapPin, Siren } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { ICONS, SpriteIcon } from "@/lib/icons";
import topHero from "@/assets/top_hero.png";
import bottomHero from "@/assets/bottom_hero.png";
import kayaMockup from "@/assets/kayapro.png";
import kayaLogo from "@/assets/kayapro-logo.png";

const easeOut = [0.22, 1, 0.36, 1];

const ABOUT_CARDS = [
    {
        icon: ICONS.codeMonitor,
        title: "Custom Software Solutions",
        desc: "Tailored systems that automate processes and improve productivity.",
    },
    {
        icon: ICONS.people3,
        title: "Digital Platforms",
        desc: "Connecting people and businesses through innovative digital marketplaces.",
    },
    {
        icon: ICONS.cloud,
        title: "IT Solutions",
        desc: "Reliable infrastructure and technical support for your operations.",
    },
];

const SERVICES = [
    {
        icon: ICONS.codeWindow,
        title: "Software & Application Development",
        desc: "Custom software, mobile apps, web applications, and system integration built for your needs.",
    },
    {
        icon: ICONS.storefront,
        title: "Digital Platforms & Marketplaces",
        desc: "On-demand service platforms, booking systems, and digital marketplaces that connect communities.",
    },
    {
        icon: ICONS.browserImage,
        title: "Web Design & Development",
        desc: "Modern, responsive websites that establish your digital presence and support business growth.",
    },
    {
        icon: ICONS.serverStack,
        title: "IT Solutions & Infrastructure",
        desc: "Network setup, server deployment, cloud solutions, and technical support.",
    },
    {
        icon: ICONS.growthBars,
        title: "IT Consulting & Digital Transformation",
        desc: "Strategic guidance to help you modernize operations and maximize technology opportunities.",
    },
];

const KAYA_FEATURES = [
    { icon: Settings, label: "Wide Range of Services" },
    { icon: ShieldCheck, label: "Verified Service Providers" },
    { icon: MapPin, label: "Location-Based Matching" },
    { icon: Siren, label: "Emergency SOS Assistance" },
];

const WHY = [
    {
        icon: ICONS.lightbulb,
        title: "Innovation with Purpose",
        desc: "We develop technology solutions with real business needs in mind.",
    },
    {
        icon: ICONS.peopleCollab,
        title: "Solutions Built Around You",
        desc: "Every organization has different requirements. We create solutions that fit your goals.",
    },
    {
        icon: ICONS.layers,
        title: "Integrated Technology",
        desc: "From software to infrastructure, we provide a complete range of IT solutions.",
    },
    {
        icon: ICONS.lineGrowth,
        title: "Designed to Scale",
        desc: "Our solutions are built to grow with your organization and evolving needs.",
    },
];

const Label = ({ children }) => (
    <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand">
        {children}
    </span>
);

export default function CompanyProfilePage() {
    useEffect(() => {
        const prev = document.title;
        document.title =
            "Company Profile | Lynxtech IT Solutions — Technology & Digital Platforms";
        return () => {
            document.title = prev;
        };
    }, []);

    return (
        <main data-testid="company-profile-page" className="overflow-hidden">
            {/* SECTION 1 — HERO */}
            <section
                data-testid="cp-hero"
                className="relative bg-gradient-to-br from-cyan-50 via-white to-blue-50 pt-28 pb-16 sm:pt-32 lg:pt-36 lg:pb-24"
            >
                <div
                    className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-cyan-200/40 blur-3xl"
                    aria-hidden="true"
                />
                <div
                    className="pointer-events-none absolute right-0 top-0 h-80 w-80 rounded-full bg-blue-200/40 blur-3xl"
                    aria-hidden="true"
                />
                <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-8 lg:grid-cols-2 lg:gap-12 lg:px-12">
                    <div>
                        <Reveal>
                            <span className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-brand">
                                <span className="h-px w-6 bg-brand" />
                                Philippine-Based Technology Company
                            </span>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                                <span className="text-navy">Innovating Technology.</span>
                                <br />
                                <span className="text-brand">Empowering Businesses.</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
                                We build digital solutions that connect businesses,
                                institutions, and communities across the Philippines.
                            </p>
                        </Reveal>
                        <Reveal delay={0.24}>
                            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                                <Link
                                    to={{ pathname: "/", hash: "#services" }}
                                    data-testid="cp-hero-explore"
                                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(2,132,199,0.35)] transition-transform duration-200 hover:-translate-y-0.5"
                                >
                                    Explore Our Services
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to={{ pathname: "/", hash: "#contact" }}
                                    data-testid="cp-hero-contact"
                                    className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors hover:border-brand hover:text-brand"
                                >
                                    Contact Us
                                </Link>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1}>
                        <motion.img
                            src={topHero}
                            alt="Illustration of a technology team building connected digital platforms and cloud infrastructure across the Philippines"
                            initial={{ opacity: 0, scale: 0.96 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: easeOut }}
                            className="mx-auto w-full max-w-xl object-contain"
                        />
                    </Reveal>
                </div>
            </section>

            {/* SECTION 2 — ABOUT */}
            <section data-testid="cp-about" className="bg-white py-16 lg:py-24">
                <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:px-12">
                    <Reveal>
                        <Label>About Lynxtech IT Solutions</Label>
                        <h2 className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl">
                            Technology That Moves Businesses Forward
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-slate-600">
                            Lynxtech IT Solutions is a Philippine-based technology company
                            dedicated to developing innovative digital solutions, providing
                            reliable IT services, and empowering businesses, organizations,
                            and communities through technology.
                        </p>
                        <Link
                            to={{ pathname: "/", hash: "#about" }}
                            data-testid="cp-about-learn-more"
                            className="group mt-7 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-brand hover:text-brand"
                        >
                            Learn More About Us
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </Reveal>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
                        {ABOUT_CARDS.map((c, i) => (
                            <Reveal key={c.title} delay={i * 0.08}>
                                <article className="h-full rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_6px_24px_rgba(15,23,42,0.05)] transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_18px_40px_rgba(2,132,199,0.12)]">
                                    <SpriteIcon
                                        src={c.icon}
                                        alt=""
                                        size={56}
                                        className="mb-4"
                                    />
                                    <h3 className="font-display text-lg font-semibold text-navy">
                                        {c.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                        {c.desc}
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 3 — SERVICES */}
            <section
                data-testid="cp-services"
                className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-24"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                    <Reveal className="mb-12 max-w-3xl">
                        <Label>Our Services</Label>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            Comprehensive Technology Solutions
                        </h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            We provide end-to-end IT services to help businesses,
                            institutions, and organizations harness the power of technology.
                        </p>
                    </Reveal>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.title} delay={(i % 5) * 0.06}>
                                <article className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_18px_40px_rgba(2,132,199,0.12)]">
                                    <SpriteIcon
                                        src={s.icon}
                                        alt=""
                                        size={52}
                                        className="mb-4"
                                    />
                                    <h3 className="font-display text-base font-semibold leading-snug text-navy">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">
                                        {s.desc}
                                    </p>
                                    <Link
                                        to={{ pathname: "/", hash: "#services" }}
                                        className="group mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand"
                                    >
                                        Learn More
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </Link>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 4 — KAYA PRO */}
            <section data-testid="cp-kaya" className="bg-white py-16 lg:py-24">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50 p-7 shadow-[0_20px_60px_rgba(2,132,199,0.1)] sm:p-10 lg:p-14">
                            <div
                                className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-cyan-200/40 blur-3xl"
                                aria-hidden="true"
                            />
                            <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
                                <div>
                                    <Label>Our Digital Platform</Label>
                                    <div className="mt-5 flex items-center gap-4">
                                        <img
                                            src={kayaLogo}
                                            alt="Kaya Pro logo"
                                            width={68}
                                            height={68}
                                            className="h-16 w-16 rounded-2xl object-contain shadow-md sm:h-[72px] sm:w-[72px]"
                                        />
                                        <div>
                                            <div className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                                                Kaya <span className="text-brand">Pro</span>
                                            </div>
                                            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                Services Marketplace
                                            </div>
                                        </div>
                                    </div>

                                    <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
                                        Kaya Pro is a digital services marketplace developed
                                        and operated by Lynxtech IT Solutions. It connects
                                        clients with verified service providers and also
                                        features emergency assistance for police, medical,
                                        and fire-related requests.
                                    </p>

                                    <Link
                                        to={{ pathname: "/", hash: "#contact" }}
                                        data-testid="cp-kaya-explore"
                                        className="group mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(2,132,199,0.35)] transition-transform duration-200 hover:-translate-y-0.5"
                                    >
                                        Explore Kaya Pro
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </Link>

                                    <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                                        {KAYA_FEATURES.map((f) => (
                                            <div
                                                key={f.label}
                                                className="flex items-center gap-3"
                                            >
                                                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-brand shadow-sm">
                                                    <f.icon className="h-4 w-4" />
                                                </span>
                                                <span className="text-sm font-medium text-navy">
                                                    {f.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="flex justify-center lg:justify-end">
                                    <img
                                        src={kayaMockup}
                                        alt="Kaya Pro mobile application shown on two smartphones — the client marketplace and the service provider dashboard"
                                        className="w-full max-w-lg object-contain drop-shadow-2xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 5 — WHY LYNXTECH */}
            <section
                data-testid="cp-why"
                className="bg-gradient-to-b from-white to-slate-50 py-16 lg:py-24"
            >
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                    <Reveal className="mb-12 max-w-3xl">
                        <Label>Why Lynxtech</Label>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                            Your Technology Partner for Digital Growth
                        </h2>
                    </Reveal>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {WHY.map((c, i) => (
                            <Reveal key={c.title} delay={(i % 4) * 0.07}>
                                <article className="h-full rounded-2xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40 hover:shadow-[0_18px_40px_rgba(2,132,199,0.12)]">
                                    <SpriteIcon
                                        src={c.icon}
                                        alt=""
                                        size={52}
                                        className="mb-4"
                                    />
                                    <h3 className="font-display text-lg font-semibold text-navy">
                                        {c.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                                        {c.desc}
                                    </p>
                                </article>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 6 — LET'S BUILD THE FUTURE TOGETHER */}
            <section data-testid="cp-cta" className="bg-white pb-20 pt-4 lg:pb-28">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 p-7 shadow-[0_20px_60px_rgba(2,132,199,0.1)] sm:p-10 lg:p-14">
                            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
                                <div>
                                    <h2 className="font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                                        Let's Build the Future Together
                                    </h2>
                                    <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-600">
                                        Whether you are a business seeking a customized
                                        software solution, an institution planning digital
                                        transformation, or an organization looking for a
                                        reliable technology partner, Lynxtech IT Solutions
                                        is ready to help.
                                    </p>
                                    <Link
                                        to={{ pathname: "/", hash: "#contact" }}
                                        data-testid="cp-cta-start"
                                        className="group mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(2,132,199,0.35)] transition-transform duration-200 hover:-translate-y-0.5"
                                    >
                                        Start a Conversation
                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                    </Link>
                                </div>
                                <div className="flex justify-center lg:justify-end">
                                    <img
                                        src={bottomHero}
                                        alt="Illustration of a team reviewing business analytics and planning digital transformation together"
                                        className="w-full max-w-xl object-contain"
                                    />
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
